import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PrintOptions from './PrintOptions';
import { getProductDetails } from '../apiClient'; // Import the API function
import { SessionAuth } from 'supertokens-auth-react/recipe/session';

const ConfigureMockupPage = () => {
  const location = useLocation();
  const { selectedProduct, selectedImage } = location.state || {};
  const [userFullName, setUserFullName] = useState('');
  const [displayedProductImage, setDisplayedProductImage] = useState(selectedProduct ? selectedProduct.image : '');
  const [productDetails, setProductDetails] = useState(null); // State for detailed product data
  const [selectedPlacement, setSelectedPlacement] = useState(null); // State for selected placement
  const [printArea, setPrintArea] = useState({ width: 0, height: 0 }); // State for print area dimensions
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [size, setSize] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [liveProductData, setLiveProductData] = useState(selectedProduct); // To hold updated data

  useEffect(() => {
    // Fetch detailed product data when the component mounts
    const fetchProductDetails = async () => {
      if (selectedProduct) {
        try {
          const details = await getProductDetails(selectedProduct.id);
          setProductDetails(details);
        } catch (error) {
          console.error("Error fetching product details:", error);
          // Handle error appropriately
        }
      }
    };

    fetchProductDetails();
    setUserFullName("John Doe"); 
  }, [selectedProduct]);

  useEffect(() => {
    if (productDetails && !selectedPlacement) {
      // Set the default placement when product details are loaded
      const defaultPlacement = productDetails.options.find(option => option.id === 'placement')?.values[0];
      if (defaultPlacement) {
        setSelectedPlacement(defaultPlacement.id);
      }
    }
  }, [productDetails, selectedPlacement]);

  useEffect(() => {
    if (productDetails && selectedPlacement) {
      // Find the print area dimensions for the selected placement
      const placementFile = productDetails.files.find(file => file.type === selectedPlacement);
      if (placementFile) {
        setPrintArea({ width: placementFile.width, height: placementFile.height });
      }
    }
  }, [productDetails, selectedPlacement]);

  if (!liveProductData || !selectedImage) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-3xl font-bold mb-6">Error: Missing Data</h1>
        <p>Please select a product and an image to configure a mockup.</p>
        {/* Optionally, add navigation back to product catalog or gallery */}
      </div>
    );
  }

  return (
    <SessionAuth>
      <div className="container mx-auto p-6 bg-background rounded-lg shadow-lg tour-configure-mockup">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Configure Your Mockup</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Selected Product Display */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Selected Product</h2>
            <img src={displayedProductImage} alt={liveProductData.name} className="w-full h-64 object-contain mb-4" />
            <h3 className="text-lg font-bold text-gray-800">{liveProductData.name}</h3>
            <p className="text-gray-600 text-sm">Estimated Price: ${liveProductData.selling_price ? liveProductData.selling_price.toFixed(2) : 'N/A'}</p>
            <p className="text-xs text-gray-500 mt-2">Ships to: {liveProductData.coverage_locations && liveProductData.coverage_locations.length > 0 ? liveProductData.coverage_locations.join(', ') : 'Worldwide'}</p>
            <p className="text-xs text-gray-500 mt-1">Fulfillment: {liveProductData.avg_fulfillment_time ? `${liveProductData.avg_fulfillment_time} business days` : '5-7 business days'}</p>
            {liveProductData.shipping_estimate && <p className="text-xs text-green-600 font-bold mt-1">Est. Delivery: {liveProductData.shipping_estimate}</p>}
          </div>

          {/* Selected Image Display */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Selected Image</h2>
            <img src={selectedImage.image_url} alt={selectedImage.prompt} className="w-full h-64 object-contain mb-4" />
            <p className="text-gray-600">Prompt: {selectedImage.prompt}</p>
            <p className="text-gray-600">Style: {selectedImage.style}</p>
          </div>
        </div>

        {/* Print Options Component */}
        <div className="tour-print-options">
          <PrintOptions 
            image={selectedImage} 
            userFullName={userFullName} 
            selectedProduct={liveProductData} // Pass the live product data
            onProductImageSelected={setDisplayedProductImage} // Pass the setter for the displayed image
            productDetails={productDetails} // Pass productDetails
            position={position}
            size={size}
            rotation={rotation}
            printArea={printArea}
            onMockupGenerated={(mockupDetails) => {
              setDisplayedProductImage(mockupDetails.mockup_image_url);
              // Update the product data to include the new shipping estimate and price
              setLiveProductData(prev => ({...prev, ...mockupDetails}));
            }}
          />
        </div>
      </div>
    </SessionAuth>
  );
};

export default ConfigureMockupPage;